// Petits blocs du détail et du formulaire : historique, contexte technique,
// suggestions de doublons, état de l'agent, états vides. Portés de
// FeedbackEditHistory, FeedbackContextCapture, FeedbackSimilarSuggestions,
// FeedbackAgentStatusDot (+ feedback-agent-status), FeedbackEmptyState.
import type { ComponentChildren, JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import type { AgentStatus, ConsoleError, EditEntry, FeedbackType, SimilarItem } from '@feedbaacks/sdk'
import { useFeedbaacks } from '../context'
import type { Translate } from '../i18n'
import { Code, External, History, Lightbulb } from '../icons'
import { Disclosure, Prose } from '../primitives'
import { absoluteDate, cx, formatRelative } from '../util'
import { StatusPill, TYPE_META } from './pills'

export function EditHistoryList({ history }: { history: EditEntry[] }): JSX.Element {
  const { t, locale } = useFeedbaacks()
  return (
    <ol class="fb-history">
      {[...history].reverse().map((e, i) => (
        <li key={`${e.at}-${i}`} class="fb-card">
          <div class="fb-faint">{t('history_replaced_on', { date: absoluteDate(e.at, locale) })}</div>
          <div class="fb-strong">{e.title}</div>
          {e.body && <Prose text={e.body} class="fb-muted" />}
        </li>
      ))}
    </ol>
  )
}

export function EditHistory({ history }: { history?: EditEntry[] }): JSX.Element | null {
  const { t } = useFeedbaacks()
  if (!history?.length) return null
  return (
    <Disclosure title={`${t('history_title')} · ${history.length}`} icon={<History size={12} />}>
      <EditHistoryList history={history} />
    </Disclosure>
  )
}

function Field({ label, children }: { label: string; children: ComponentChildren }): JSX.Element {
  return (
    <div class="fb-field-row">
      <span class="fb-faint">{label}</span>
      <div class="fb-field-value">{children}</div>
    </div>
  )
}

/** Contexte technique en lecture (équipe : le serveur ne l'envoie qu'à elle). */
export function ContextRead({ pageUrl, viewport, userAgent, screenshotUrl, consoleErrors }: {
  pageUrl?: string
  viewport?: string
  userAgent?: string
  screenshotUrl?: string
  consoleErrors?: unknown
}): JSX.Element | null {
  const { t } = useFeedbaacks()
  const errors = Array.isArray(consoleErrors) ? (consoleErrors as ConsoleError[]) : []
  if (!pageUrl && !viewport && !userAgent && !screenshotUrl && errors.length === 0) return null
  return (
    <Disclosure title={t('context_title')} icon={<Code size={14} />}>
      <Field label={t('context_url')}>
        {pageUrl ? (
          <a href={pageUrl} target="_blank" rel="noreferrer noopener" class="fb-mono fb-link">
            {pageUrl} <External size={12} />
          </a>
        ) : '-'}
      </Field>
      <Field label={t('context_viewport')}><span class="fb-mono">{viewport ?? '-'}</span></Field>
      <Field label={t('context_user_agent')}><span class="fb-mono">{userAgent ?? '-'}</span></Field>
      {screenshotUrl && (
        <Field label={t('context_capture')}>
          <a href={screenshotUrl} target="_blank" rel="noreferrer noopener" aria-label={t('context_screenshot_open')}>
            <img class="fb-context-shot" src={screenshotUrl} alt={t('context_screenshot_alt')} data-testid="feedbaacks-context-screenshot" />
          </a>
        </Field>
      )}
      {errors.length > 0 && (
        <Field label={t('context_console_errors', { count: errors.length })}>
          <pre class="fb-console">{errors.map((e) => `${e.ts}  ${e.message}`).join('\n')}</pre>
        </Field>
      )}
    </Disclosure>
  )
}

/** Contexte technique modifiable au dépôt (équipe). */
export function ContextEdit({ pageUrl, onPageUrl, viewport, userAgent, screenshot, consoleCount, consoleEnabled, onConsoleEnabled }: {
  pageUrl: string
  onPageUrl: (v: string) => void
  viewport: string
  userAgent: string
  screenshot?: ComponentChildren
  consoleCount?: number
  consoleEnabled: boolean
  onConsoleEnabled: (v: boolean) => void
}): JSX.Element {
  const { t } = useFeedbaacks()
  return (
    <Disclosure title={t('context_title')} icon={<Code size={14} />}>
      <Field label={t('context_url')}>
        <input class="fb-input fb-mono" type="url" value={pageUrl} spellcheck={false} onInput={(e) => onPageUrl(e.currentTarget.value)} />
      </Field>
      <Field label={t('context_viewport')}><span class="fb-mono">{viewport || '-'}</span></Field>
      <Field label={t('context_user_agent')}><span class="fb-mono">{userAgent || '-'}</span></Field>
      {screenshot && <Field label={t('context_capture')}>{screenshot}</Field>}
      {consoleCount != null && (
        <label class="fb-check">
          <input type="checkbox" checked={consoleEnabled} onChange={(e) => onConsoleEnabled(e.currentTarget.checked)} />
          <span>{t('context_console_include', { count: consoleCount })}</span>
        </label>
      )}
    </Disclosure>
  )
}

function closedLabel(item: SimilarItem, t: Translate, locale: string): string | null {
  if (!item.resolved_at) return null
  const since = formatRelative(item.resolved_at, locale, t)
  if (item.status === 'shipped') return t('similar_shipped', { since })
  if (item.status === 'declined') return t('similar_declined', { since })
  if (item.status === 'duplicate') return t('similar_duplicate', { since })
  return null
}

export function SimilarSuggestions({ items, onPreview }: { items: SimilarItem[]; onPreview: (id: string) => void }): JSX.Element | null {
  const { t, locale } = useFeedbaacks()
  if (!items.length) return null
  return (
    <div class="fb-similar" role="region" aria-label={t('similar_aria')} data-testid="feedbaacks-similar">
      <div class="fb-similar-title">
        <Lightbulb size={12} />
        {t('similar_title')}
      </div>
      <ul>
        {items.slice(0, 3).map((it) => {
          const closed = closedLabel(it, t, locale)
          return (
            <li key={it.id}>
              <button type="button" class="fb-similar-item" onClick={() => onPreview(it.id)}>
                <span class="fb-grow">
                  <span class="fb-clamp-1">{it.title}</span>
                  <span class="fb-faint">{t('similar_posted', { since: formatRelative(it.created_at, locale, t) })}</span>
                </span>
                {closed && <span class="fb-faint">{closed}</span>}
                <StatusPill status={it.status} />
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const STALE_MS = 25 * 60 * 1000

/** Projection pure état publié → rendu (testée). */
export function projectAgentStatus(s: AgentStatus | null, now: number, t: Translate, locale: string): { tone: string; pulse: boolean; label: string } {
  const time = (iso: string) => new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  if (!s || s.state === 'unknown') return { tone: 'gray', pulse: false, label: t('agent_no_signal') }
  const cap = s.capacity_pct != null ? t('agent_capacity', { pct: s.capacity_pct }) : ''
  const updated = s.updated_at ? new Date(s.updated_at).getTime() : 0
  if (now - updated > STALE_MS) {
    return { tone: 'gray', pulse: false, label: t('agent_stale', { time: s.updated_at ? time(s.updated_at) : '?' }) + cap }
  }
  if (s.state === 'limited') {
    return { tone: 'amber', pulse: false, label: t('agent_limited', { time: s.limit_resets_at ? time(s.limit_resets_at) : '?' }) + cap }
  }
  if (s.state === 'blocked') return { tone: 'red', pulse: false, label: t('agent_blocked', { detail: s.detail ?? '' }) + cap }
  if (s.state === 'working') {
    return { tone: 'accent', pulse: true, label: t('agent_working', { detail: s.detail ?? '', queue: s.queue_count ?? 0 }) + cap }
  }
  const low = s.capacity_pct != null && s.capacity_pct < 20
  return { tone: low ? 'amber' : 'green', pulse: false, label: t('agent_idle') + cap }
}

/** Point d'état de l'agent de traitement : admins seulement. */
export function AgentStatusDot(): JSX.Element | null {
  const { client, role, t, locale } = useFeedbaacks()
  const [status, setStatus] = useState<AgentStatus | null>(null)
  const [now, setNow] = useState(0)
  useEffect(() => {
    if (role !== 'admin') return
    let live = true
    const load = () =>
      client.agent.status().then((s) => {
        if (!live) return
        setStatus(s)
        setNow(Date.now())
      }, () => {})
    void load()
    const timer = setInterval(load, 60_000)
    return () => {
      live = false
      clearInterval(timer)
    }
  }, [client, role])
  if (role !== 'admin') return null
  const view = projectAgentStatus(status, now, t, locale)
  return <span class={cx('fb-agent-dot', `is-${view.tone}`, view.pulse && 'is-pulse')} title={view.label} aria-label={view.label} role="img" data-testid="feedbaacks-agent-dot" data-state={status?.state ?? 'unknown'} />
}

export function EmptyFirst({ onCreate }: { onCreate: (type: FeedbackType) => void }): JSX.Element {
  const { t, options } = useFeedbaacks()
  const types = useAppTypes()
  if (options.slots?.empty) return <>{options.slots.empty()}</>
  return (
    <div class="fb-empty" data-testid="feedbaacks-empty-first">
      <span class="fb-empty-orb" aria-hidden="true" />
      <h3>{t('empty_first_title_v2')}</h3>
      <p class="fb-muted">{t('empty_first_subtitle_v2')}</p>
      <div class="fb-empty-ctas" role="group" aria-label={t('empty_first_title_v2')}>
        {types.map((type) => {
          const M = TYPE_META[type]
          return (
            <button key={type} type="button" class={cx('fb-empty-cta', `fb-type-${type}`)} onClick={() => onCreate(type)} data-testid={`feedbaacks-empty-cta-${type}`}>
              <M.Icon size={20} />
              <span>{t(M.key)}</span>
            </button>
          )
        })}
      </div>
      {options.shortcut !== false && <p class="fb-faint">{t('empty_first_shortcut_hint')}</p>}
    </div>
  )
}

export function EmptyFiltered({ onClear }: { onClear: () => void }): JSX.Element {
  const { t } = useFeedbaacks()
  return (
    <div class="fb-empty" data-testid="feedbaacks-empty-filtered">
      <p class="fb-muted">
        {t('empty_filtered_inline')}{' '}
        <button type="button" class="fb-link" onClick={onClear}>{t('filters_reset')}</button>
      </p>
    </div>
  )
}

/** Types proposés par l'app (session du widget) ; tous pour le tableau. */
export function useAppTypes(): FeedbackType[] {
  const { client } = useFeedbaacks()
  const [types, setTypes] = useState<FeedbackType[]>(['feature', 'bug', 'question'])
  useEffect(() => {
    if (client.authType !== 'session') return
    client.currentSession().then((s) => setTypes(s.app.types), () => {})
  }, [client])
  return types
}
